/**
 * Piecewise-linear segmentation: split a series into contiguous phases that
 * are each well-described by a straight line.
 *
 * Unlike mean-shift (CUSUM) detection — which only sees changes in *level* —
 * a linear-cost split detects changes in *slope* too. A rise-then-fall peak,
 * a level step, and a gradual ramp are handled correctly: the peak and the
 * step both split, while a clean ramp stays a single "rose steadily" phase.
 *
 * The method is top-down binary segmentation. For each candidate split it
 * compares the residual error of one line over the whole range against two
 * lines over the halves, and splits on the candidate that reduces residual
 * error the most — but only if that reduction clears a sensitivity threshold
 * (as a fraction of the unsplit error) and both halves satisfy the minimum
 * size/duration guards. This is what prevents over-segmentation into noise.
 */
import { linearRegression, mean, standardDeviation } from 'simple-statistics';

import { MS_PER_DAY, MS_PER_HOUR } from '#core/lib/utils/date.ts';

import { EPSILON } from './constants.ts';
import { computeTrend } from './trend.ts';
import type { Segment, SegmentTransition, Sensitivity, TimeseriesPoint } from './types.ts';

/**
 * Minimum fraction of the unsplit residual error a split must remove to be
 * accepted. Lower → splits more eagerly → more segments.
 */
const SENSITIVITY_MIN_GAIN: Record<Sensitivity, number> = {
  low: 0.5,
  medium: 0.3,
  high: 0.15,
};

export interface SegmentationOptions {
  /** Granularity knob; controls the split-acceptance threshold. Default `'medium'`. */
  sensitivity?: Sensitivity;
  /** Minimum points per segment. Default 3. */
  minSegmentPoints?: number;
  /** Minimum segment span as a fraction of the total span. Default 0.05. */
  minSegmentFraction?: number;
  /** Significance threshold passed through to per-segment trend. Default 0.05. */
  alpha?: number;
}

interface ResolvedOptions {
  minGain: number;
  minPoints: number;
  minSpanMs: number;
  alpha: number;
}

/** Residual sum of squares of the least-squares line through the points. */
function lineRss(points: TimeseriesPoint[]): number {
  const first = points[0];
  // A line fits any one or two points exactly.
  if (points.length < 3 || !first) {
    return 0;
  }
  const pairs = points.map((p) => [(p.timestamp - first.timestamp) / MS_PER_DAY, p.value] as [number, number]);
  const { m, b } = linearRegression(pairs);
  return pairs.reduce((sum, [x, y]) => sum + (y - (m * x + b)) ** 2, 0);
}

/**
 * Recursively find split indices within the inclusive range `[lo, hi]`. Each
 * returned index is the start of a new (right-hand) segment.
 */
function findSplits(points: TimeseriesPoint[], lo: number, hi: number, opts: ResolvedOptions): number[] {
  const n = hi - lo + 1;
  const pLo = points[lo];
  const pHi = points[hi];
  if (n < 2 * opts.minPoints || !pLo || !pHi) {
    return [];
  }

  const base = lineRss(points.slice(lo, hi + 1));
  if (base <= EPSILON) {
    return []; // already linear — nothing to gain
  }

  // Candidate split starts: index `s` begins the right segment [s, hi]; the
  // left segment is [lo, s - 1]. Both must clear the point and span guards.
  const candidates = Array.from({ length: n - 2 * opts.minPoints + 1 }, (_, i) => lo + opts.minPoints + i);
  const scored = candidates
    .filter((s) => {
      const left = points[s - 1];
      const right = points[s];
      if (!left || !right) {
        return false;
      }
      const leftSpan = left.timestamp - pLo.timestamp;
      const rightSpan = pHi.timestamp - right.timestamp;
      return leftSpan >= opts.minSpanMs && rightSpan >= opts.minSpanMs;
    })
    .map((s) => ({
      s,
      gain: base - lineRss(points.slice(lo, s)) - lineRss(points.slice(s, hi + 1)),
    }));

  const best = scored.reduce((a, b) => (b.gain > a.gain ? b : a), { s: -1, gain: Number.NEGATIVE_INFINITY });
  if (best.s < 0 || best.gain / base < opts.minGain) {
    return [];
  }

  return [...findSplits(points, lo, best.s - 1, opts), best.s, ...findSplits(points, best.s, hi, opts)];
}

/** Plain-language phrasing of a phase transition for a non-technical reader. */
function transitionLabel(type: SegmentTransition['type']): string {
  if (type === 'jump') {
    return 'sharp jump';
  }
  if (type === 'drop') {
    return 'sharp drop';
  }
  return 'turned a corner';
}

/** Classify the level change between two consecutive phases. */
function describeTransition(prevMean: number, currMean: number, seriesStdev: number): SegmentTransition {
  const magnitude = currMean - prevMean;
  // A "marked" level change is one comparable to the spread of the whole
  // series; smaller changes are read as a turn in slope rather than a step.
  const isLevelShift = seriesStdev > EPSILON && Math.abs(magnitude) > 0.5 * seriesStdev;
  const stepType = magnitude > 0 ? 'jump' : 'drop';
  const type: SegmentTransition['type'] = isLevelShift ? stepType : 'slope-change';
  return { label: transitionLabel(type), magnitude, type };
}

/**
 * Segment a series into near-linear phases. Each segment carries its own
 * trend and the transition that begins it.
 */
export function segmentSeries(points: TimeseriesPoint[], options: SegmentationOptions = {}): Segment[] {
  const firstPoint = points[0];
  const lastPoint = points.at(-1);
  if (!firstPoint || !lastPoint) {
    return [];
  }

  const sensitivity = options.sensitivity ?? 'medium';
  const totalSpanMs = lastPoint.timestamp - firstPoint.timestamp;
  const opts: ResolvedOptions = {
    minGain: SENSITIVITY_MIN_GAIN[sensitivity],
    minPoints: options.minSegmentPoints ?? 3,
    minSpanMs: (options.minSegmentFraction ?? 0.05) * totalSpanMs,
    alpha: options.alpha ?? 0.05,
  };

  const splits = findSplits(points, 0, points.length - 1, opts);
  const bounds = [0, ...splits, points.length];
  const seriesStdev = standardDeviation(points.map((p) => p.value));

  // Each span is one segment: [lo, hi] inclusive. The mean level of the
  // previous span classifies the transition into the current one.
  const spans = bounds.slice(0, -1).map((lo, i) => ({ lo, hi: (bounds[i + 1] ?? points.length) - 1 }));
  const spanMean = (span: { lo: number; hi: number }): number =>
    mean(points.slice(span.lo, span.hi + 1).map((p) => p.value));

  return spans.flatMap((span, i): Segment[] => {
    const loPoint = points[span.lo];
    const hiPoint = points[span.hi];
    if (!loPoint || !hiPoint) {
      return [];
    }

    const slice = points.slice(span.lo, span.hi + 1);
    const prev = spans[i - 1];

    return [
      {
        start: loPoint.timestamp,
        end: hiPoint.timestamp,
        durationHours: (hiPoint.timestamp - loPoint.timestamp) / MS_PER_HOUR,
        pointCount: slice.length,
        trend: computeTrend(slice, { alpha: opts.alpha }),
        transition: prev === undefined ? null : describeTransition(spanMean(prev), spanMean(span), seriesStdev),
      },
    ];
  });
}
